import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronRight, MessageCircle, Send, Play, FileText, Download, X, ChevronLeft, ChevronRight as ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, buildWhatsAppUrl, productWhatsAppMessage } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import ProductCard from "@/components/ProductCard";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import useSEO, { canonicalUrl } from "@/hooks/useSEO";
import { useUIOverlay } from "@/lib/uiOverlay";

const STATUS_STYLES = {
  "Tersedia": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Terjual": "bg-neutral-100 text-neutral-500 border-neutral-200",
  "Segera Hadir": "bg-[#FFF4E8] text-[#E9840D] border-[#FFD3A2]",
  "Hubungi Kami": "bg-[#F8F8F8] text-[#171717] border-[#E5E5E5]",
};

const CONDITION_STYLES = {
  "Baru": "bg-emerald-600 text-white",
  "Rekondisi": "bg-[#2C5AA0] text-white",
  "Bekas": "bg-neutral-700 text-white",
};

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "h2", "h3", "h4", "a", "blockquote"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
  RETURN_TRUSTED_TYPE: false,
};

const isRealImage = (url) => {
  if (!url) return false;
  const u = String(url).trim();
  if (!u) return false;
  if (u.includes("images.unsplash.com")) return false;
  return true;
};

/** Convert admin-entered video URL into a safe embed spec. Returns null if invalid. */
function parseVideoEmbed(video) {
  if (!video || !video.published || !video.url) return null;
  const url = String(video.url).trim();
  const type = video.type || "youtube";
  if (type === "direct") {
    if (!/^https?:\/\//i.test(url)) return null;
    return { kind: "direct", src: url };
  }
  if (type === "vimeo") {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (m) return { kind: "iframe", src: `https://player.vimeo.com/video/${m[1]}?dnt=1`, provider: "Vimeo", videoId: m[1] };
    return null;
  }
  // YouTube: watch, youtu.be, shorts, embed
  const m = url.match(/(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{11})/i);
  if (m) {
    const id = m[1];
    return {
      kind: "iframe",
      src: `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`,
      provider: "YouTube",
      videoId: id,
      thumb: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    };
  }
  return null;
}

/** Sanitize HTML then check whether it contains anything printable. */
function safeHtml(raw) {
  if (!raw) return { html: "", empty: true };
  const clean = DOMPurify.sanitize(String(raw), SANITIZE_CONFIG);
  const text = clean.replace(/<[^>]*>/g, "").trim();
  return { html: clean, empty: text.length === 0 };
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const { settings } = useSettings();
  const wa = settings?.whatsapp || "";

  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", company: "", city: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Mobile-only compact UX toggles. Desktop always renders the full content.
  const [descOpen, setDescOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const MOBILE_SPEC_LIMIT = 6;
  const MOBILE_RELATED_LIMIT = 4;

  // Hide the floating WhatsApp button on this page whenever the mobile sticky
  // bottom CTA is present (mobile < lg). On desktop we keep an
  // IntersectionObserver so the floating button hides only while the primary
  // CTA row is on-screen. This eliminates the mobile overlap + scroll flicker
  // completely without adding a second state system.
  const primaryCtaRef = useRef(null);
  const { setPrimaryCtaVisible, menuOpen } = useUIOverlay();
  useEffect(() => {
    const mql = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 1023px)")
      : null;

    let obs = null;
    const applyForMobile = () => setPrimaryCtaVisible(true);
    const applyForDesktop = () => {
      const el = primaryCtaRef.current;
      if (!el || typeof IntersectionObserver === "undefined") { setPrimaryCtaVisible(false); return; }
      obs?.disconnect();
      obs = new IntersectionObserver(([entry]) => setPrimaryCtaVisible(entry.isIntersecting), { threshold: 0.1 });
      obs.observe(el);
    };

    const apply = () => {
      obs?.disconnect(); obs = null;
      if (mql?.matches) applyForMobile();
      else applyForDesktop();
    };

    apply();
    mql?.addEventListener?.("change", apply);
    return () => {
      mql?.removeEventListener?.("change", apply);
      obs?.disconnect();
      setPrimaryCtaVisible(false);
    };
  }, [setPrimaryCtaVisible, product]);

  useEffect(() => {
    setNotFound(false); setActiveImg(0); setLightboxIdx(-1); setVideoPlaying(false);
    let cancelled = false;
    api.get(`/products/slug/${slug}`)
      .then((r) => { if (!cancelled) setProduct(r.data); })
      .catch(() => {
        // Follow legacy-slug redirect (permanent). Replace history so back-nav skips 404.
        api.get(`/redirects/product/${slug}`)
          .then((r) => {
            if (cancelled) return;
            const target = r?.data?.new_slug;
            if (target && target !== slug) nav(`/produk/${target}`, { replace: true });
            else setNotFound(true);
          })
          .catch(() => { if (!cancelled) setNotFound(true); });
      });
    api.get(`/products/related/${slug}`).then((r) => setRelated(r.data)).catch(() => {});
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  const embed = useMemo(() => parseVideoEmbed(product?.video), [product]);
  const description = useMemo(() => safeHtml(product?.full_description), [product]);
  const documents = useMemo(
    () => (product?.documents || []).filter((d) => d?.url && d?.title),
    [product]
  );

  const seoTitle = product?.seo_title || (product ? `${product.product_name} | Teknik Diesel Indonesia` : (notFound ? "Produk tidak ditemukan | Teknik Diesel Indonesia" : ""));
  const seoDesc = product?.seo_description || product?.short_description || (product ? `${product.product_name} tersedia di Teknik Diesel Indonesia.` : "");
  useSEO({
    title: seoTitle,
    description: seoDesc,
    path: product?.slug ? `/produk/${product.slug}` : undefined,
    image: product?.featured_image || undefined,
    type: "product",
    noindex: notFound,
  });

  useEffect(() => {
    if (!product) return;
    // Structured data (Product + Breadcrumbs + optional Video). Title/meta/canonical
    // are already handled by useSEO above — this only owns the JSON-LD block.
    const graph = [
      {
        "@type": "Product",
        "name": product.product_name,
        "brand": { "@type": "Brand", "name": product.brand },
        "category": product.category,
        "sku": product.model,
        "description": product.short_description || seoDesc,
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Beranda", "item": "https://teknikdiesel.com/" },
          { "@type": "ListItem", "position": 2, "name": "Produk", "item": "https://teknikdiesel.com/produk" },
          { "@type": "ListItem", "position": 3, "name": product.category, "item": `https://teknikdiesel.com/produk?category=${encodeURIComponent(product.category)}` },
          { "@type": "ListItem", "position": 4, "name": product.product_name, "item": canonicalUrl(`/produk/${product.slug}`) },
        ],
      },
    ];
    // Only add VideoObject when a real, published video is configured — no fabrication.
    if (embed && product.video?.published) {
      graph.push({
        "@type": "VideoObject",
        "name": product.video.title || `Video ${product.product_name}`,
        "description": product.video.description || product.short_description || `Video produk ${product.product_name}`,
        "thumbnailUrl": product.video.thumbnail || embed.thumb || product.featured_image || undefined,
        "uploadDate": product.updated_at || product.created_at || undefined,
        "embedUrl": embed.src,
      });
    }
    const ld = { "@context": "https://schema.org", "@graph": graph };
    document.head.querySelector("#ld-product")?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "ld-product";
    s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => { document.head.querySelector("#ld-product")?.remove(); };
  }, [product, embed]);

  // Lightbox keyboard nav
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const nextLightbox = useCallback(() => {
    if (!product) return;
    setLightboxIdx((i) => (i + 1) % images.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);
  const prevLightbox = useCallback(() => {
    if (!product) return;
    setLightboxIdx((i) => (i - 1 + images.length) % images.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLightbox();
      if (e.key === "ArrowLeft") prevLightbox();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) { toast.error("Nama dan WhatsApp wajib diisi"); return; }
    setSubmitting(true);
    try {
      await api.post("/enquiries/product", {
        product_id: product.id,
        product_name: product.product_name,
        ...form,
        message: form.message
          ? `${form.message}\n\n— Halaman: ${window.location.href}`
          : `Enquiry dari halaman: ${window.location.href}`,
      });
      toast.success("Permintaan berhasil dikirim! Tim kami akan menghubungi Anda.");
      setForm({ name: "", whatsapp: "", email: "", company: "", city: "", message: "" });
    } catch { toast.error("Gagal mengirim. Silakan coba lagi."); }
    finally { setSubmitting(false); }
  };

  const scrollToEnquiry = () => {
    setFormOpen(true); // expand the mobile-collapsed form so the user lands on real inputs
    // give React a tick to render the form before we scroll
    setTimeout(() => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="text-7xl font-display font-black text-[#FF9418] mb-3">404</div>
        <h1 className="font-display font-bold text-[#171717] text-3xl mb-3">Produk tidak ditemukan</h1>
        <p className="text-[#555555] mb-6">Produk yang Anda cari mungkin sudah tidak tersedia.</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => nav("/")} className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg">Kembali ke Beranda</Button>
          <Button onClick={() => nav("/produk")} variant="outline" className="border-[#E5E5E5] text-[#171717] rounded-lg">Lihat Produk</Button>
        </div>
      </div>
    );
  }

  if (!product) return <div className="text-center py-24 text-[#737373]">Memuat...</div>;

  const gallery = (product.gallery_images || []).filter((g) => isRealImage(g?.url));
  const featured = isRealImage(product.featured_image) ? { url: product.featured_image, alt: product.product_name } : null;
  const images = featured ? [featured, ...gallery] : gallery;
  const hasImages = images.length > 0;
  const mainImg = images[activeImg]?.url;

  const status = product.stock_status || "Hubungi Kami";
  const condition = product.condition || "Hubungi Kami";
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES["Hubungi Kami"];

  const videoPoster = product.video?.thumbnail || embed?.thumb || product.featured_image || "";
  const galleryVideoThumb = embed && videoPoster ? videoPoster : null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-xs text-[#737373] mb-6 font-mono-tech" data-testid="breadcrumb">
        <Link to="/" className="hover:text-[#FF9418]">Beranda</Link>
        <ChevronRight size={12} />
        <Link to="/produk" className="hover:text-[#FF9418]">Produk</Link>
        <ChevronRight size={12} />
        <Link to={`/produk?category=${encodeURIComponent(product.category)}`} className="hover:text-[#FF9418]">{product.category}</Link>
        <ChevronRight size={12} />
        <span className="text-[#171717] truncate">{product.product_name}</span>
      </nav>

      {/* HERO */}
      <div className="grid lg:grid-cols-2 gap-10 mb-14">
        <div>
          <div
            className={`relative aspect-[4/3] bg-white border border-[#E5E5E5] rounded-xl overflow-hidden mb-3 ${hasImages ? "cursor-zoom-in" : ""}`}
            onClick={() => hasImages && setLightboxIdx(activeImg)}
            data-testid="product-main-image-wrap"
          >
            {hasImages ? (
              <img src={mainImg} alt={product.product_name} className="w-full h-full object-cover" data-testid="product-main-image" />
            ) : (
              <ProductPlaceholder brand={product.brand} model={product.model} alt={`Foto ${product.product_name} belum tersedia`} />
            )}
            <div className="absolute top-4 left-4 flex flex-col gap-1.5">
              <span className={`px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-wider border rounded ${statusStyle}`}>{status}</span>
              {product.condition && CONDITION_STYLES[product.condition] && (
                <span className={`px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded ${CONDITION_STYLES[product.condition]}`}>{product.condition}</span>
              )}
            </div>
          </div>
          {(images.length > 1 || galleryVideoThumb) && (
            <div className="grid grid-cols-5 gap-2">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square border rounded overflow-hidden ${i === activeImg ? "border-[#FF9418]" : "border-[#E5E5E5]"}`} data-testid={`thumb-${i}`}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {galleryVideoThumb && (
                <button
                  onClick={() => document.getElementById("video-section")?.scrollIntoView({ behavior: "smooth" })}
                  className="aspect-square border border-[#E5E5E5] rounded overflow-hidden relative group bg-black"
                  data-testid="gallery-video-thumb"
                  title="Lihat video produk"
                >
                  <img src={galleryVideoThumb} alt="Video" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
                    <div className="w-8 h-8 rounded-full bg-[#FF9418] flex items-center justify-center shadow-lg">
                      <Play size={14} className="text-white ml-0.5" fill="currentColor" />
                    </div>
                    <span className="text-[9px] font-mono-tech uppercase tracking-wider text-white font-bold">▶ Video</span>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] mb-2 font-semibold">{product.brand} • {product.category}</div>
          <h1 className="font-display font-extrabold text-[#171717] text-3xl lg:text-4xl leading-tight mb-4" data-testid="product-title">{product.product_name}</h1>
          {product.short_description && <p className="text-[#555555] leading-relaxed mb-6">{product.short_description}</p>}

          <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 mb-5" data-testid="product-info-card">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[#737373] text-[10px] font-mono-tech uppercase tracking-widest">Brand</div>
                <div className="text-[#171717] font-semibold">{product.brand}</div>
              </div>
              <div>
                <div className="text-[#737373] text-[10px] font-mono-tech uppercase tracking-widest">Model</div>
                <div className="text-[#171717] font-semibold">{product.model}</div>
              </div>
              <div>
                <div className="text-[#737373] text-[10px] font-mono-tech uppercase tracking-widest">Ketersediaan</div>
                <div className="text-[#171717] font-semibold">{status}</div>
              </div>
              <div>
                <div className="text-[#737373] text-[10px] font-mono-tech uppercase tracking-widest">Kondisi</div>
                <div className="text-[#171717] font-semibold">{condition}</div>
              </div>
            </div>
          </div>

          <div ref={primaryCtaRef} className="flex flex-col sm:flex-row gap-3 mb-6">
            <Button
              onClick={scrollToEnquiry}
              className="flex-1 h-12 bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg uppercase tracking-wider font-bold text-sm shadow-md"
              data-testid="cta-minta-penawaran"
            >
              <Send size={16} /> Minta Penawaran
            </Button>
            {wa && (
              <a href={buildWhatsAppUrl(wa, productWhatsAppMessage(product.product_name))} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full h-12 border-2 border-[#22C55E] text-[#16A34A] hover:bg-[#22C55E] hover:text-white rounded-lg uppercase tracking-wider font-bold text-sm bg-white"
                  data-testid="cta-wa"
                >
                  <MessageCircle size={16} /> Tanya via WhatsApp
                </Button>
              </a>
            )}
          </div>

          <div className="text-xs text-[#737373] font-mono-tech uppercase tracking-wider">Butuh informasi lengkap? Hubungi tim kami untuk mendapatkan penawaran terbaik.</div>
        </div>
      </div>

      {/* DESKRIPSI PRODUK (sanitized HTML) */}
      {!description.empty && (
        <section className="mb-14" data-testid="description-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-0.5 bg-[#FF9418]" />
            <h2 className="font-display font-extrabold text-[#171717] text-2xl">Deskripsi Produk</h2>
          </div>
          <div
            className={`product-rich-text text-[#333333] leading-relaxed max-w-4xl ${descOpen ? "" : "line-clamp-6 lg:line-clamp-none"}`}
            dangerouslySetInnerHTML={{ __html: description.html }}
            data-testid="description-content"
          />
          <button
            type="button"
            onClick={() => setDescOpen((v) => !v)}
            className="lg:hidden mt-3 text-sm font-semibold text-[#FF9418] hover:text-[#E9840D] inline-flex items-center gap-1"
            aria-expanded={descOpen}
            data-testid="desc-toggle"
          >
            {descOpen ? "Tampilkan Lebih Sedikit" : "Lihat Selengkapnya"}
          </button>
        </section>
      )}

      {/* SPESIFIKASI TEKNIS */}
      <section className="mb-14" data-testid="spec-table-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-0.5 bg-[#FF9418]" />
          <h2 className="font-display font-extrabold text-[#171717] text-2xl">Spesifikasi Teknis</h2>
        </div>
        {product.specifications && product.specifications.length > 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {product.specifications.map((s, i) => (
                  <tr key={i} className={`border-b border-[#E5E5E5] last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-[#F8F8F8]"} ${!specOpen && i >= MOBILE_SPEC_LIMIT ? "hidden lg:table-row" : ""}`} data-testid={`spec-row-${i}`}>
                    <td className="px-4 py-3 text-[#737373] font-mono-tech text-xs uppercase tracking-wider md:w-1/3 align-top">{s.name}</td>
                    <td className="px-4 py-3 text-[#171717] break-words">{s.value || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {product.specifications.length > MOBILE_SPEC_LIMIT && (
              <button
                type="button"
                onClick={() => setSpecOpen((v) => !v)}
                className="lg:hidden w-full border-t border-[#E5E5E5] py-3 text-sm font-semibold text-[#FF9418] hover:bg-[#FFF4E8]"
                aria-expanded={specOpen}
                data-testid="spec-toggle"
              >
                {specOpen ? "Tampilkan Lebih Sedikit" : `Lihat Semua Spesifikasi (${product.specifications.length})`}
              </button>
            )}
          </div>
        ) : (
          <div className="p-6 bg-[#F8F8F8] border-l-4 border-[#FF9418] rounded">
            <p className="text-sm text-[#555555] m-0">
              Spesifikasi teknis belum ditampilkan. Hubungi Teknik Diesel Indonesia untuk mendapatkan spesifikasi lengkap unit ini.
            </p>
          </div>
        )}
      </section>

      {/* VIDEO PRODUK */}
      {embed && (
        <section id="video-section" className="mb-14 scroll-mt-24" data-testid="video-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-0.5 bg-[#FF9418]" />
            <h2 className="font-display font-extrabold text-[#171717] text-2xl">Video Produk</h2>
          </div>
          {product.video?.title && <div className="font-display font-bold text-[#171717] text-lg mb-3">{product.video.title}</div>}
          <div className="relative w-full max-w-4xl bg-black rounded-xl overflow-hidden border border-[#E5E5E5]" style={{ aspectRatio: "16 / 9" }}>
            {!videoPlaying ? (
              <button
                type="button"
                onClick={() => setVideoPlaying(true)}
                className="absolute inset-0 w-full h-full group"
                data-testid="video-play-btn"
                aria-label="Play video"
              >
                {videoPoster && <img src={videoPoster} alt={product.video?.title || "Video preview"} className="w-full h-full object-cover" loading="lazy" />}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#FF9418] flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                    <Play size={30} className="text-white ml-1" fill="currentColor" />
                  </div>
                </div>
              </button>
            ) : embed.kind === "direct" ? (
              <video src={embed.src} controls playsInline poster={videoPoster || undefined} className="absolute inset-0 w-full h-full" data-testid="video-player-direct" />
            ) : (
              <iframe
                src={`${embed.src}${embed.src.includes("?") ? "&" : "?"}autoplay=1`}
                title={product.video?.title || `Video ${product.product_name}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="video-player-iframe"
              />
            )}
          </div>
          {product.video?.description && <p className="text-sm text-[#555555] mt-3 max-w-4xl leading-relaxed">{product.video.description}</p>}
        </section>
      )}

      {/* GALERI PRODUK */}
      {images.length > 0 && (
        <section className="mb-14" data-testid="gallery-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-0.5 bg-[#FF9418]" />
            <h2 className="font-display font-extrabold text-[#171717] text-2xl">Galeri Produk</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setLightboxIdx(i)}
                className="aspect-square bg-white border border-[#E5E5E5] rounded-lg overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#FF9418]"
                data-testid={`gallery-tile-${i}`}
              >
                <img src={img.url} alt={img.alt || product.product_name} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* DOKUMEN PRODUK */}
      {documents.length > 0 && (
        <section className="mb-14" data-testid="documents-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-0.5 bg-[#FF9418]" />
            <h2 className="font-display font-extrabold text-[#171717] text-2xl">Dokumen Produk</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
            {documents.map((d, i) => {
              const isPdf = /\.pdf($|\?)/i.test(d.url);
              const fullUrl = d.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : d.url;
              return (
                <a
                  key={d.id || i}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-[#E5E5E5] hover:border-[#FF9418] rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-3 transition group"
                  data-testid={`doc-${d.id || i}`}
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#FFF4E8] text-[#FF9418] rounded-md flex items-center justify-center flex-shrink-0">
                    <FileText size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-[#737373]">{d.doc_type || "Dokumen"}</div>
                    <div className="font-semibold text-[#171717] text-sm truncate">{d.title}</div>
                  </div>
                  <div className="text-[11px] font-mono-tech uppercase tracking-wider text-[#FF9418] flex items-center gap-1 whitespace-nowrap">
                    {isPdf ? <><span className="hidden sm:inline">Download PDF</span><Download size={14} /></> : <><span className="hidden sm:inline">Lihat</span><ArrowRight size={14} /></>}
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* MINTA PENAWARAN FORM (mobile-collapsible; desktop always inline) */}
      <section id="enquiry-form" className="mb-14 scroll-mt-24" data-testid="enquiry-form-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-0.5 bg-[#FF9418]" />
          <h2 className="font-display font-extrabold text-[#171717] text-2xl">Minta Penawaran</h2>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="lg:hidden w-full bg-white border border-[#E5E5E5] hover:border-[#FF9418] rounded-xl px-4 py-4 flex items-center justify-between transition"
            aria-expanded={formOpen}
            data-testid="mobile-open-form"
          >
            <span className="flex items-center gap-3">
              <span className="w-9 h-9 bg-[#FFF4E8] text-[#FF9418] rounded-md flex items-center justify-center"><Send size={16} /></span>
              <span className="text-sm font-semibold text-[#171717]">Isi form penawaran</span>
            </span>
            <span className="text-xs font-mono-tech uppercase tracking-wider text-[#FF9418]">Buka</span>
          </button>
        )}
        <form onSubmit={submitEnquiry} className={`bg-white border border-[#E5E5E5] rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4 ${formOpen ? "" : "hidden lg:grid"}`}>
          <Input label="Nama *" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="enquiry-name" required />
          <Input label="Nomor WhatsApp *" value={form.whatsapp} onChange={(v) => setForm({...form, whatsapp: v})} testid="enquiry-whatsapp" required />
          <Input label="Email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="enquiry-email" type="email" />
          <Input label="Perusahaan" value={form.company} onChange={(v) => setForm({...form, company: v})} testid="enquiry-company" />
          <Input label="Kota" value={form.city} onChange={(v) => setForm({...form, city: v})} testid="enquiry-city" />
          <Input label="Produk" value={product.product_name} onChange={() => {}} readOnly testid="enquiry-product" />
          <div className="md:col-span-2">
            <label className="text-xs font-mono-tech uppercase tracking-wider text-[#737373] mb-1.5 block">Pesan</label>
            <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={4} className="w-full bg-white border border-[#E5E5E5] focus:border-[#FF9418] rounded px-3 py-2 text-sm text-[#171717] outline-none" data-testid="enquiry-message" />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <Button type="submit" disabled={submitting} className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-11 px-8 uppercase tracking-wider font-bold text-sm shadow-md" data-testid="enquiry-submit">
              <Send size={14} /> {submitting ? "Mengirim..." : "Kirim Permintaan"}
            </Button>
            <Button type="button" onClick={() => setFormOpen(false)} variant="outline" className="lg:hidden border-[#E5E5E5] text-[#737373] rounded-lg h-11 px-5">Tutup</Button>
          </div>
        </form>
      </section>

      {/* PRODUK TERKAIT — limited on mobile, full grid on desktop */}
      {related.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-0.5 bg-[#FF9418]" />
            <h2 className="font-display font-extrabold text-[#171717] text-2xl">Produk Terkait</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((p, i) => (
              <div key={p.id} className={i >= MOBILE_RELATED_LIMIT ? "hidden sm:block" : ""}><ProductCard product={p} /></div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to={`/produk?category=${encodeURIComponent(product.category)}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF9418] hover:text-[#E9840D]" data-testid="related-see-more">
              Lihat Produk Lainnya di {product.category} <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* Mobile sticky bottom CTA — hidden when the main CTA is on-screen or the mobile menu is open */}
      {wa && (
        <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] p-3 shadow-2xl grid grid-cols-2 gap-2 transition-opacity duration-200 ${menuOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`} data-testid="mobile-sticky-cta">
          <Button onClick={scrollToEnquiry} className="h-11 bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg uppercase tracking-wider font-bold text-xs">
            <Send size={14} /> Minta Penawaran
          </Button>
          <a href={buildWhatsAppUrl(wa, productWhatsAppMessage(product.product_name))} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full h-11 border-2 border-[#22C55E] text-[#16A34A] hover:bg-[#22C55E] hover:text-white rounded-lg uppercase tracking-wider font-bold text-xs bg-white">
              <MessageCircle size={14} /> WhatsApp
            </Button>
          </a>
        </div>
      )}
      <div className="lg:hidden h-20" aria-hidden="true"></div>

      {/* LIGHTBOX */}
      {lightboxIdx >= 0 && images[lightboxIdx] && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={closeLightbox} data-testid="lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white/80 hover:text-white p-2" data-testid="lightbox-close" aria-label="Close">
            <X size={28} />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevLightbox(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/40 rounded-full" data-testid="lightbox-prev" aria-label="Previous">
                <ChevronLeft size={28} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextLightbox(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/40 rounded-full" data-testid="lightbox-next" aria-label="Next">
                <ArrowRight size={28} />
              </button>
            </>
          )}
          <img src={images[lightboxIdx].url} alt={images[lightboxIdx].alt || product.product_name} className="max-w-full max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-xs font-mono-tech">{lightboxIdx + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", required, readOnly }) {
  return (
    <div>
      <label className="text-xs font-mono-tech uppercase tracking-wider text-[#737373] mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} required={required}
        className="w-full bg-white border border-[#E5E5E5] focus:border-[#FF9418] rounded px-3 py-2 text-sm text-[#171717] outline-none disabled:opacity-70" data-testid={testid} />
    </div>
  );
}
